/**
 * Browser side of error tracking (RFC reliability-and-support §2.7, §3.1).
 * Client-safe: no server imports.
 *
 *   reportClientError(err)   POST /api/client-errors (keepalive), at most
 *                            5 reports per page load — an error loop must
 *                            not flood the endpoint
 *   recentClientErrors()     the last 10 error messages seen on this page
 *                            load; the support form offers to attach them
 *   installClientErrorListeners()  window `error` + `unhandledrejection`
 */

const MAX_REPORTS = 5
const RING = 10

let reported = 0
const recent: string[] = []

function remember(message: string): void {
  recent.push(`${new Date().toISOString().slice(11, 19)} ${message}`.slice(0, 300))
  if (recent.length > RING) recent.shift()
}

export function recentClientErrors(): string[] {
  return [...recent]
}

export function reportClientError(err: unknown, route?: string): void {
  if (typeof window === 'undefined') return
  const e = err instanceof Error ? err : new Error(typeof err === 'string' ? err : 'Unknown client error')
  remember(e.message)
  if (reported >= MAX_REPORTS) return
  reported++
  try {
    void fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        message: e.message.slice(0, 2000),
        name: e.name,
        stack: e.stack?.slice(0, 4000),
        route: route ?? window.location.pathname,
      }),
    }).catch(() => undefined)
  } catch {
    // Reporting must never throw into the page.
  }
}

let installed = false

export function installClientErrorListeners(): () => void {
  if (typeof window === 'undefined' || installed) return () => undefined
  installed = true
  const onError = (ev: ErrorEvent) => {
    // Cross-origin script errors carry no detail — nothing to act on.
    if (!ev.error && (!ev.message || ev.message === 'Script error.')) return
    reportClientError(ev.error ?? ev.message)
  }
  const onRejection = (ev: PromiseRejectionEvent) => {
    const r = ev.reason
    // A fetch the user navigated away from is not a bug.
    if (r && typeof r === 'object' && (r as { name?: string }).name === 'AbortError') return
    reportClientError(r)
  }
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)
  return () => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
    installed = false
  }
}
